import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { formatDate } from '@angular/common';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import * as XLSX from 'xlsx';
import { Centre } from '../../../../interfaces/Centres';
import { CentreService } from '../../../../services/centre.service';
import { OmShantiRow, OmShantiService } from './om-shanti.service';

interface CentreYearRow {
  centreCode: string;
  centreName: string;
  perYear: { [year: number]: number };
  unknown: number;
  total: number;
}

interface CentreRangeRow {
  centreCode: string;
  centreName: string;
  count: number;
}

@Component({
  selector: 'app-om-shanti-report',
  templateUrl: './om-shanti-report.component.html',
  styleUrls: ['./om-shanti-report.component.scss']
})
export class OmShantiReportComponent implements OnInit, OnDestroy {
  fileName = 'OM_SHANTI_Report.xlsx';
  isLoading = false;

  // ── Shared filters (apply to every tab) ───────────────────────────────────
  centres$: Observable<Centre[]>;
  selectedCentreCodes: string[] = [];
  /** 'All' | 'Exact' | 'Estimated' */
  basisFilter: 'All' | 'Exact' | 'Estimated' = 'All';

  // ── Tab 1: detail list ───────────────────────────────────────────────────
  displayedColumns: string[] = [
    'position',
    'childCode',
    'childName',
    'gender',
    'centreName',
    'dateOfOmShanti',
    'dateBasis',
    'modeOmShanti',
    'requestStatus',
    'remark'
  ];
  startDate?: Date | null;
  endDate?: Date | null;
  totalCount = 0;
  exactCount = 0;
  estimatedCount = 0;
  dataSource = new MatTableDataSource<OmShantiRow>([]);

  // ── Tab 2: count by centre × year ────────────────────────────────────────
  yearOptions: number[] = [];
  fromYear = 2023;
  toYear = new Date().getFullYear();
  includeUnknown = true;
  years: number[] = [];
  showUnknownColumn = false;
  yearRows: CentreYearRow[] = [];
  yearTotals: { [year: number]: number } = {};
  unknownTotal = 0;
  grandTotal = 0;

  // ── Tab 3: count by centre for a date range ──────────────────────────────
  rangeStart?: Date | null;
  rangeEnd?: Date | null;
  rangeRows: CentreRangeRow[] = [];
  rangeTotal = 0;
  rangeExcludesUndated = 0;

  private allRows: OmShantiRow[] = [];
  private _destroyed$ = new Subject<void>();

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  constructor(
    private readonly centreService: CentreService,
    private readonly omShantiService: OmShantiService,
    private readonly snackBar: MatSnackBar
  ) {
    this.centres$ = this.centreService.getAllCentres();
    const current = new Date().getFullYear();
    for (let y = current; y >= 2015; y--) this.yearOptions.push(y);
  }

  ngOnInit(): void {
    this.loadData();
  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
    this.dataSource.sortingDataAccessor = (row: OmShantiRow, id: string) => {
      if (id === 'dateOfOmShanti') return row.dateOfOmShanti ? row.dateOfOmShanti.getTime() : 0;
      return (row as any)[id];
    };
  }

  ngOnDestroy(): void {
    this._destroyed$.next();
    this._destroyed$.complete();
  }

  loadData(): void {
    this.isLoading = true;
    this.omShantiService
      .fetchRows()
      .pipe(takeUntil(this._destroyed$))
      .subscribe({
        next: rows => {
          this.allRows = rows;
          this.recompute();
          this.isLoading = false;
        },
        error: err => {
          console.error('OM SHANTI report load failed', err);
          this.snackBar.open('Failed to load OM SHANTI data', '', { duration: 3000, panelClass: 'error' });
          this.isLoading = false;
        }
      });
  }

  /** Re-runs every tab. Called on load and whenever a shared filter changes. */
  recompute(): void {
    this.applyDetailFilters();
    this.buildYearTable();
    this.buildRangeTable();
  }

  /** Rows left after the shared centre + basis filters. */
  private sharedFiltered(): OmShantiRow[] {
    let rows = [...this.allRows];
    if (this.basisFilter === 'Exact') rows = rows.filter(r => r.dateBasis === 'Exact');
    else if (this.basisFilter === 'Estimated') rows = rows.filter(r => r.dateBasis !== 'Exact');
    if (this.selectedCentreCodes.length) {
      rows = rows.filter(r => this.selectedCentreCodes.includes(r.centreCode));
    }
    return rows;
  }

  // ── Tab 1 ────────────────────────────────────────────────────────────────
  applyDetailFilters(): void {
    let rows = this.sharedFiltered();

    if (this.startDate) {
      const start = new Date(this.startDate);
      start.setHours(0, 0, 0, 0);
      rows = rows.filter(r => r.dateOfOmShanti && r.dateOfOmShanti >= start);
    }
    if (this.endDate) {
      const end = new Date(this.endDate);
      end.setHours(23, 59, 59, 999);
      rows = rows.filter(r => r.dateOfOmShanti && r.dateOfOmShanti <= end);
    }

    this.dataSource.data = rows;
    if (this.dataSource.paginator) this.dataSource.paginator.firstPage();

    this.totalCount = rows.length;
    this.exactCount = rows.filter(r => r.dateBasis === 'Exact').length;
    this.estimatedCount = this.totalCount - this.exactCount;
  }

  applyTextFilter(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.dataSource.filter = value.trim().toLowerCase();
    if (this.dataSource.paginator) this.dataSource.paginator.firstPage();
  }

  clearDetailFilters(): void {
    this.startDate = null;
    this.endDate = null;
    this.applyDetailFilters();
  }

  // ── Tab 2 ────────────────────────────────────────────────────────────────
  buildYearTable(): void {
    if (this.fromYear > this.toYear) {
      const t = this.fromYear;
      this.fromYear = this.toYear;
      this.toYear = t;
    }

    this.years = [];
    for (let y = this.fromYear; y <= this.toYear; y++) this.years.push(y);

    const rows = this.sharedFiltered();
    const byCentre = new Map<string, CentreYearRow>();
    const yearSet = new Set(this.years);
    this.yearTotals = {};
    this.years.forEach(y => (this.yearTotals[y] = 0));
    this.unknownTotal = 0;

    const getCentre = (r: OmShantiRow): CentreYearRow => {
      const code = r.centreCode || '—';
      if (!byCentre.has(code)) {
        const perYear: { [year: number]: number } = {};
        this.years.forEach(y => (perYear[y] = 0));
        byCentre.set(code, { centreCode: code, centreName: r.centreName || code, perYear, unknown: 0, total: 0 });
      }
      return byCentre.get(code)!;
    };

    rows.forEach(r => {
      const d = r.dateOfOmShanti;
      if (!d) {
        if (!this.includeUnknown) return;
        const c = getCentre(r);
        c.unknown++;
        c.total++;
        this.unknownTotal++;
        return;
      }
      const y = d.getFullYear();
      if (!yearSet.has(y)) return;
      const c = getCentre(r);
      c.perYear[y]++;
      c.total++;
      this.yearTotals[y]++;
    });

    this.showUnknownColumn = this.includeUnknown && this.unknownTotal > 0;
    this.yearRows = [...byCentre.values()].sort((a, b) => a.centreName.localeCompare(b.centreName));
    this.grandTotal =
      this.years.reduce((s, y) => s + this.yearTotals[y], 0) +
      (this.showUnknownColumn ? this.unknownTotal : 0);
  }

  clearYearFilters(): void {
    this.fromYear = 2023;
    this.toYear = new Date().getFullYear();
    this.includeUnknown = true;
    this.buildYearTable();
  }

  // ── Tab 3 ────────────────────────────────────────────────────────────────
  buildRangeTable(): void {
    const rows = this.sharedFiltered();

    let start: Date | null = null;
    let end: Date | null = null;
    if (this.rangeStart) {
      start = new Date(this.rangeStart);
      start.setHours(0, 0, 0, 0);
    }
    if (this.rangeEnd) {
      end = new Date(this.rangeEnd);
      end.setHours(23, 59, 59, 999);
    }

    const byCentre = new Map<string, CentreRangeRow>();
    this.rangeExcludesUndated = 0;

    rows.forEach(r => {
      const d = r.dateOfOmShanti;
      if (!d) {
        this.rangeExcludesUndated++;
        return;
      }
      if (start && d < start) return;
      if (end && d > end) return;
      const code = r.centreCode || '—';
      if (!byCentre.has(code)) {
        byCentre.set(code, { centreCode: code, centreName: r.centreName || code, count: 0 });
      }
      byCentre.get(code)!.count++;
    });

    this.rangeRows = [...byCentre.values()].sort((a, b) => a.centreName.localeCompare(b.centreName));
    this.rangeTotal = this.rangeRows.reduce((s, c) => s + c.count, 0);
  }

  clearRange(): void {
    this.rangeStart = null;
    this.rangeEnd = null;
    this.buildRangeTable();
  }

  // ── Shared ───────────────────────────────────────────────────────────────
  clearSharedFilters(): void {
    this.selectedCentreCodes = [];
    this.basisFilter = 'All';
    this.recompute();
  }

  // ── Exports ──────────────────────────────────────────────────────────────
  downloadDetailReport(): void {
    const rows = this.dataSource.filteredData.length ? this.dataSource.filteredData : this.dataSource.data;
    const data = rows.map((r, i) => ({
      'Sr No': i + 1,
      'Child Code': r.childCode,
      'Child Name': r.childName,
      Gender: r.gender,
      Centre: r.centreName || r.centreCode,
      'Date of OM SHANTI': this.display(r.dateOfOmShanti),
      'Date Basis': r.dateBasis,
      'Mode of Confirmation': r.modeOmShanti,
      'Request Status': r.requestStatus,
      'Date of Admission': this.display(r.dateOfAdmission),
      Remark: r.remark
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'OM SHANTI');
    XLSX.writeFile(wb, this.fileName);
  }

  downloadYearReport(): void {
    this.exportTable('om-shanti-year-table', 'OM SHANTI Count', 'OM_SHANTI_Count_By_Year.xlsx');
  }

  downloadRangeReport(): void {
    this.exportTable('om-shanti-range-table', 'OM SHANTI Range', 'OM_SHANTI_Count_By_Range.xlsx');
  }

  private exportTable(elementId: string, sheet: string, file: string): void {
    const element = document.getElementById(elementId);
    const ws: XLSX.WorkSheet = XLSX.utils.table_to_sheet(element);
    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, sheet);
    XLSX.writeFile(wb, file);
  }

  display(d: Date | null): string {
    return d ? formatDate(d, 'dd/MM/yyyy', 'en-IN') : '';
  }
}
